import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, User, LogOut } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { useLogout } from '@/hooks/use-logout';
import { useTranslations } from '@/i18n/useTranslations';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { RoleSwitcherMenuItems } from '@/components/auth/role-switcher';

/**
 * 账号下拉(引导式顶栏右侧)。补引导式缺失的账号/退出登录入口。
 * 头像首字母触发(AuthUser 无 photoUrl,账号级无头像照片) → 切换角色 / 设置 / 退出登录。
 * 复用 ui/dropdown-menu(base-ui)、auth/role-switcher 的 RoleSwitcherMenuItems、hooks/use-logout。
 */
export function AccountMenu() {
  const t = useTranslations('guided');
  const tAuth = useTranslations('auth');
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const { logout, isPending } = useLogout();
  const [open, setOpen] = useState(false);

  const initial = (user?.displayName || '?').trim().slice(0, 1).toUpperCase() || '?';
  const roleLabel = user?.activeRole ? t(`role.${user.activeRole}`) : '';

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger
        data-testid="account-trigger"
        aria-label={t('accountLabel')}
        className="inline-flex items-center gap-0.5 rounded-full p-0.5 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
          {initial}
        </span>
        <ChevronDown className="h-3.5 w-3.5" />
      </DropdownMenuTrigger>
      <DropdownMenuContent side="bottom" align="end" className="w-56">
        {/* 头部:姓名 + 当前角色(只读)。base-ui GroupLabel 必须在 Group 内,否则抛 MenuGroupContext missing */}
        <DropdownMenuGroup>
          <DropdownMenuLabel className="normal-case tracking-normal">
            <span className="block text-sm font-medium normal-case text-foreground">
              {user?.displayName || '?'}
            </span>
            {roleLabel && (
              <span className="block text-xs normal-case text-muted-foreground">{roleLabel}</span>
            )}
          </DropdownMenuLabel>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        {/* 角色切换(单角色用户由 RoleSwitcherMenuItems 自身返回 null) */}
        <RoleSwitcherMenuItems onSwitchSuccess={() => setOpen(false)} />
        <DropdownMenuItem onClick={() => navigate('/dashboard/profiles')}>
          <User className="h-4 w-4" />
          {t('myProfiles')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" disabled={isPending} onClick={logout}>
          <LogOut className="h-4 w-4" />
          {tAuth('logout.title')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default AccountMenu;
